import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DATE_TIME_FORMAT, MAX_FILE_SIZE, YES_NO_OPTIONS } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import {
    AuthService,
    PermissionType,
    AccessType,
    DataSources,
    ShuntLocationType,
    TrailerType,
    EmptyCheckOutsClient,
    EmptyCheckOutDto,
    EmptyCheckOutStatus,
    EmptyCheckOutAction,
    ShuntEmptyCheckOutCommand,
    UpdateEmptyCheckOutCommand,
} from '@services';
import EnumUtils from '../../../../../shared/enum-utils';
import notify from 'devextreme/ui/notify';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { CustomStore } from 'devextreme-aspnet-data-nojquery';
import { EmptyCheckOutToolbarComponent } from './components/empty-check-out-toolbar/empty-check-out-toolbar.component';
import { EmptyCheckOutStatusPipe } from 'src/app/pipes/empty-check-out-status.pipe';
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';
import { ShuntMoveFormComponent } from 'src/app/pages/yard-management/shunt-management/shunt-move-form/shunt-move-form.component';

@Component({
    selector: 'app-empty-check-out-details',
    imports: [
        CommonModule,
        EmptyCheckOutStatusPipe,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTagBoxModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        EmptyCheckOutToolbarComponent,
        ShuntMoveFormComponent,
        RouterLink,
        NotesComponent,
    ],
    templateUrl: './empty-check-out-details.component.html',
})
export class EmptyCheckOutDetailsComponent implements OnInit {
    @Input() number: string | null = null;
    @ViewChild('validationGroup', { static: true }) validationGroup: DxValidationGroupComponent;
    @ViewChild(ShuntMoveFormComponent, { static: false }) createShuntMoveForm: ShuntMoveFormComponent;
    @ViewChild(DxScrollViewComponent, { static: false }) scrollView?: DxScrollViewComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private route = inject(ActivatedRoute);
    private location = inject(Location);
    protected auth = inject(AuthService);
    protected dataSources = inject(DataSources);
    private emptyCheckOutClient = inject(EmptyCheckOutsClient);
    protected readonly NoteType = NoteType;
    public dateTimeFormat = DATE_TIME_FORMAT;
    protected carrierDataSource = this.dataSources.carrierOptions();
    protected shipmentDataSource = this.dataSources.shipmentOptions();
    protected emptyCheckOut: EmptyCheckOutDto = new EmptyCheckOutDto();
    protected readonly EmptyCheckOutStatus = EmptyCheckOutStatus;

    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isReadOnly: boolean = false;
    maxFileSize: number = MAX_FILE_SIZE;
    isAddShuntMovePopupOpened = false;
    pendingNoteAction: EmptyCheckOutAction | null = null;
    shuntLocationDataSource: CustomStore;
    shuntLocationOptions = EnumUtils.getEnumOptions(ShuntLocationType);
    trailerTypeOptions = EnumUtils.getEnumOptions(TrailerType);
    shuntLocation = ShuntLocationType;

    public yesNoOptions = YES_NO_OPTIONS;

    public requireDetails = false;

    get isLoadedVariant(): boolean {
        return (
            this.emptyCheckOut.status === EmptyCheckOutStatus.ReadyForCheckoutLoaded ||
            this.emptyCheckOut.status === EmptyCheckOutStatus.CheckedOutLoaded
        );
    }

    public customValidator = (params: { value: any }): boolean => {
        if (!this.requireDetails) return true;
        return (
            params.value !== null &&
            params.value !== undefined &&
            params.value !== ''
        );
    };

    ngOnInit(): void {
        this.number = this.route.snapshot.paramMap.get('number');
        this.loadEmptyCheckOut();
    }

    private loadEmptyCheckOut() {
        this.isLoading = true;
        this.emptyCheckOutClient.getEmptyCheckOut(this.number!).subscribe({
            next: async (data) => {
                this.emptyCheckOut = data;
                this.isReadOnly = !this.auth.checkPermission(PermissionType.EmptyCheckOutDetails, AccessType.Manage) ||
                    this.emptyCheckOut.status === EmptyCheckOutStatus.CheckedOut ||
                    this.emptyCheckOut.status === EmptyCheckOutStatus.CheckedOutEmpty ||
                    this.emptyCheckOut.status === EmptyCheckOutStatus.CheckedOutLoaded;
                this.shipmentDataSource = this.dataSources.shipmentOptions(data.shipments ?? []);
                this.isLoading = false;
            },
            error: () => {
                notify('Failed to load check-out', 'error', 3000);
                this.isLoading = false;
                this.location.back();
            },
        });
    }

    protected getBadgeClass(status: EmptyCheckOutStatus): string {
        return EnumUtils.getEmptyCheckOutStatusBadgeClass(status);
    }

    addShuntMove() {
        this.isAddShuntMovePopupOpened = true;
    }

    onShuntMovePopupSaveClick() {
        this.closePopupAndSave(EmptyCheckOutAction.ShuntMove, () => {
            this.isAddShuntMovePopupOpened = false;
        });
    }

    private closePopupAndSave(
        action: EmptyCheckOutAction,
        closePopup: () => void,
    ) {
        closePopup();
        this.save(action);
    }

    async onSaveClick(action: EmptyCheckOutAction) {     
        if(action === EmptyCheckOutAction.Investigate ||
                action === EmptyCheckOutAction.InvestigationCompleted ||
                action === EmptyCheckOutAction.CancelMove)
        {
            this.pendingNoteAction = action;
            this.notesComponent.openCreatePopup();

        } else{
            this.save(action);
        }
    }

    onNoteSaved() {
        if (!this.pendingNoteAction)
        {
            return;
        }

        const action = this.pendingNoteAction;
        this.pendingNoteAction = null;
        this.save(action);
    }

    onNoteCanceled() {
        this.pendingNoteAction = null;
    }

    async save(action: EmptyCheckOutAction) {
        this.requireDetails = action === EmptyCheckOutAction.CheckOut;
        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const request$ = this.buildRequest$(action);

        request$.subscribe({
            next: async (number) => {
                notify('Check-out updated successfully', 'success', 3000);
                this.isSubmitting = false;
                this.loadEmptyCheckOut();
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    'Failed to update check-out';
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    private buildRequest$(action: EmptyCheckOutAction) {
        const number = this.number as string;

        switch (action) {
            case EmptyCheckOutAction.ShuntMove: {
                const shuntMoveData = this.createShuntMoveForm.getData();
                const command = new ShuntEmptyCheckOutCommand({
                    ...this.emptyCheckOut,
                    shuntMoveLocation: shuntMoveData.location,
                    shuntMoveLocationId: shuntMoveData.locationId,
                    priority: shuntMoveData.priority,
                    note: shuntMoveData.note,
                    number: number,
                    action,
                });
                return this.emptyCheckOutClient.shuntEmptyCheckOut(command);
            }
            default: {
                const command = new UpdateEmptyCheckOutCommand({
                    ...this.emptyCheckOut,
                    number: number,
                    action,
                });
                return this.emptyCheckOutClient.updateEmptyCheckOut(command);
            }
        }
    }

    cancel() {
        this.location.back();
    }
}
